<template>
  <sliding-tabs
      :activeTab="jobsStatusAlias"
      :disabled="disabled"
      @change="handleClick"
      class="JOB-TABS">
    <sliding-tabs-content
        v-for="(tab, index) in tabs"
        :key="tab.key"
        :name="tab.key"
        :titleProps="{
          title: tab.label,
          titleClass: index ? 'text-capitalize' : 'FIRST-TAB-TITLE',
          suffix: getSuffix(tab.key),
          suffixClass: index ? undefined : 'BUBBLE',
          to: {
            name: 'dashboard::pro::jobs',
            params: { jobsStatusAlias: tab.key },
            query: $route.query,
          },
        }"
    />
  </sliding-tabs>
</template>

<script>
import { mapGetters } from 'vuex';
import tweenjs from '@tweenjs/tween.js';

import jobParams from '@/mixins/job-params';
import jobs from '@/mixins/jobs';
import counter from '@/filters/counter';
import SlidingTabs from '@/components/SlidingTabs';
import SlidingTabsContent from '@/components/SlidingTabs/SlidingTabsContent';

export default {
  counter,
  name: 'job-tabs',
  components: {
    SlidingTabs,
    SlidingTabsContent,
  },
  mixins: [
    jobParams,
    jobs,
  ],
  data () {
    return {
      tabs: [
        this.$options.JOBS_STATUS_ALIASES.new,
        this.$options.JOBS_STATUS_ALIASES.potential,
        this.$options.JOBS_STATUS_ALIASES.hired,
        this.$options.JOBS_STATUS_ALIASES.completed,
        this.$options.JOBS_STATUS_ALIASES.past,
      ],
      tweenGroup: null,
      animationFrameRequestId: null,
      newTotal: 0,
      potentialTotal: 0,
      hiredTotal: 0,
      completedTotal: 0,
      pastTotal: 0,
      disabled: false,
    };
  },
  computed: {
    ...mapGetters('account/pro/info', [
      'serviceProviderId',
    ]),
    newTotalComputed () {
      return this.getJobsMetaByStatusAlias(
        this.$options.JOBS_STATUS_ALIASES.new.key,
        this.workOrdersMatchesKey
      ).total;
    },
    potentialTotalComputed () {
      return this.getJobsMetaByStatusAlias(
        this.$options.JOBS_STATUS_ALIASES.potential.key,
        this.workOrdersMatchesKey
      ).total;
    },
    hiredTotalComputed () {
      return this.getJobsMetaByStatusAlias(
        this.$options.JOBS_STATUS_ALIASES.hired.key,
        this.workOrdersMatchesKey
      ).total;
    },
    completedTotalComputed () {
      return this.getJobsMetaByStatusAlias(
        this.$options.JOBS_STATUS_ALIASES.completed.key,
        this.workOrdersMatchesKey
      ).total;
    },
    pastTotalComputed () {
      return this.getJobsMetaByStatusAlias(
        this.$options.JOBS_STATUS_ALIASES.past.key,
        this.workOrdersMatchesKey
      ).total;
    },
  },
  watch: {
    newTotalComputed () {
      this.tween('newTotal');
    },
    potentialTotalComputed () {
      this.tween('potentialTotal');
    },
    hiredTotalComputed () {
      this.tween('hiredTotal');
    },
    completedTotalComputed () {
      this.tween('completedTotal');
    },
    pastTotalComputed () {
      this.tween('pastTotal');
    },
  },
  mounted () {
    this.tweenGroup = new tweenjs.Group();
    this.tabs.forEach(({ key, statuses }) => {
      if (this[`${key}TotalComputed`] || key === this.jobsStatusAlias) {
        this.tween(`${key}Total`);
      } else {
        this.fetchWorkOrderMatches({
          jobsStatusAlias: key,
          params: {
            statuses: statuses.join(','),
            size: 0,
            service_provider_id: this.serviceProviderId,
          },
        });
      }
    });
    this.animate();
  },
  destroyed () {
    global.cancelAnimationFrame(this.animationFrameRequestId);

    this.tweenGroup.removeAll();
  },
  methods: {
    handleClick () {
      this.disabled = true;
      setTimeout(() => {
        this.disabled = false;
      }, 700);
    },
    animate () {
      this.animationFrameRequestId = global.requestAnimationFrame(this.animate);

      this.tweenGroup.update();
    },
    tween (key) {
      if (this.tweenGroup) {
        const from = this[key];
        const to = this[`${key}Computed`];
        const diff = Math.ceil(Math.abs(Math.min(to, 100) - Math.min(from, 100)));
        const speed = 666 / Math.max(diff, 1);

        return new tweenjs.Tween(this.$data, this.tweenGroup)
          .to({ [key]: to }, speed * diff)
          .start();
      }
    },
    getSuffix (tabKey) {
      if ([
        this.$options.JOBS_STATUS_ALIASES.past.key,
        this.$options.JOBS_STATUS_ALIASES.completed.key,
      ].includes(tabKey)) {
        return undefined;
      }
      // Always use the computed value for the tab count, not the animated value
      const computedKey = `${tabKey}TotalComputed`;
      const value = typeof this[computedKey] !== 'undefined' ? this[computedKey] : this[`${tabKey}Total`];
      return this.$options.counter(Number(value).toFixed(0));
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  .JOB-TABS {
    height: 42px;
    display: flex;
    flex: 0 0 auto;
    border-bottom: 1px solid $color-gray30;
  }

  /deep/ .FIRST-TAB-TITLE {
    @include margin-left(1, $md: 4);
    text-transform: capitalize;
  }

  /deep/ .BUBBLE {
    min-width: 22px;
    padding: 0 0.75*$unit;
    display: inline-block;
    vertical-align: middle;
    line-height: 22px;
    color: white;
    background-color: $color-depot;
    border-radius: 22px;

    &:before,
    &:after {
      content: '';
    }
  }
</style>
